import {
    AccountClass,
    AccountType,
    AccountSubtype,
} from "../../../constants/domains/accounting/accounts";
import { Form1099Box } from "@common/constants/domains/accounting/form1099Box";

export enum AccountRole {
    /** Identifies the fallback account for uncategorized incoming activity. */
    uncategorizedIncome = "uncategorized_income",
    /** Identifies the fallback account for uncategorized outgoing activity. */
    uncategorizedExpense = "uncategorized_expense",
    /** Identifies the default receivable account for customer balances. */
    accountsReceivable = "accounts_receivable",
    /** Identifies the default payable account for vendor balances. */
    accountsPayable = "accounts_payable",
    /** Identifies the fallback revenue account when no income account is assigned. */
    defaultRevenue = "default_revenue",
    /** Identifies the expense account for uncollectible customer balances. */
    badDebtExpense = "bad_debt_expense",
    /** Identifies the account representing cash held directly by the business. */
    cashOnHand = "cash_on_hand",
    /** Identifies the clearing account for transfers between bank accounts. */
    bankTransferClearing = "bank_transfer_clearing",
    /** Identifies the clearing account for payment-processor transfers. */
    paymentTransferClearing = "payment_transfer_clearing",
    /** Identifies the clearing account for credit-card payments. */
    creditCardClearing = "credit_card_clearing",
    /** Identifies the clearing account for payroll activity. */
    payrollClearing = "payroll_clearing",
    /** Identifies the clearing account for employee reimbursements. */
    reimbursementsClearing = "reimbursements_clearing",
    /** Identifies the liability account for collected sales tax. */
    salesTaxPayable = "sales_tax_payable",
    /** Identifies the liability account for customer deposits. */
    customerDeposits = "customer_deposits",
    /** Identifies the contra-revenue account for discounts and allowances. */
    discountsAndAllowances = "discounts_and_allowances",
    /** Identifies the asset account for deposits paid on bills. */
    prepaidExpenses = "prepaid_expenses",
    /** Identifies the equity account for retained earnings or owner's capital. */
    retainedEarningsOrOwnersCapital = "retained_earnings_or_owners_capital",
    /** Identifies the equity account for owner contributions. */
    ownerContributions = "owner_contributions",
    /** Identifies the equity account for owner withdrawals. */
    ownerDraws = "owner_draws",
    /** Identifies the generic receivable fallback when no dedicated related-business account exists. */
    intercompanyReceivable = "intercompany_receivable",
    /** Identifies the generic payable fallback when no dedicated related-business account exists. */
    intercompanyPayable = "intercompany_payable",
}

export function isSystemManagedAccount(
    account: Pick<Account, "role" | "relatedEntityId">,
): boolean {
    return account.role != null || account.relatedEntityId != null;
}

export interface Account {
    id: string;
    code: number;
    codeText: string | null;
    name: string;
    class: AccountClass;
    type: AccountType;
    subtype: AccountSubtype;
    isDisabled: boolean;
    isManuallyCreated: boolean;
    financialAccountId: number | null;
    kickOriginalCode: number | null;
    parentGroupId: string | null;
    relatedEntityId?: number | null;
    role?: AccountRole | null;
    form1099Box?: Form1099Box | null;
    sortOrder: number;
}

// Minimal shape needed to walk a group hierarchy by name. Shared by entity
// account groups and CoA-template account groups (which have a templateId
// instead of an entityId), so hierarchy helpers can operate on both.
export interface AccountGroupHierarchyNode {
    id: string;
    name: string;
    parentGroupId: string | null;
    sortOrder: number;
}

export interface AccountGroup extends AccountGroupHierarchyNode {
    entityId: number;
    type: AccountType;
}

export enum AccountStatus {
    ENABLED = "enabled",
    DISABLED = "disabled",
}

export enum AccountSource {
    CUSTOM = "custom",
    KICK = "kick",
}

export enum AccountRolePresence {
    HAS_ROLE = "has_role",
    NO_ROLE = "no_role",
}

export enum AccountRulePresence {
    HAS_RULE = "has_rule",
    NO_RULES = "no_rules",
}

export interface GetAccountsResponse {
    accounts: Account[];
}
